<template>
    <aside
        class="fixed left-0 top-0 w-64 h-screen bg-gradient-to-b from-rose-700 to-rose-900 text-white shadow-xl flex flex-col print:hidden">
        <!-- Logo/Header -->
        <div class="p-6 border-b border-rose-600">
            <h2 class="text-2xl font-bold">Heart Clinic</h2>
            <p class="text-rose-200 text-sm mt-1">Arta de a vindeca</p>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 py-6">
            <ul class="space-y-2 px-3">
                <li>
                    <button data-cy="nav-pacienti" @click="go('pacienti')" :class="navClass('pacienti')">
                        <i class="pi pi-users text-lg"></i>
                        <span class="font-medium">Pacienți</span>
                    </button>
                </li>
                <li>
                    <button data-cy="nav-istoric" @click="go('istoric')" :class="navClass('istoric')">
                        <i class="pi pi-history text-lg"></i>
                        <span class="font-medium">Istoric</span>
                    </button>
                </li>
                <li>
                    <button data-cy="nav-formular" @click="go('formular')" :class="navClass('formular')">
                        <i class="pi pi-file-edit text-lg"></i>
                        <span class="font-medium">Formular</span>
                    </button>
                </li>
                <li>
                    <button data-cy="nav-audit" @click="go('audit')" :class="navClass('audit')">
                        <i class="pi pi-book text-lg"></i>
                        <span class="font-medium">Jurnal Activitate</span>
                    </button>
                </li>
                <li>
                    <button data-cy="nav-exports" @click="go('exporturi')" :class="navClass('exporturi')">
                        <i class="pi pi-file-pdf text-lg"></i>
                        <span class="font-medium">Istoric Exporturi</span>
                    </button>
                </li>
                <li>
                    <button data-cy="nav-cont" @click="go('cont')" :class="navClass('cont')">
                        <i class="pi pi-user text-lg"></i>
                        <span class="font-medium">Cont</span>
                    </button>
                </li>
            </ul>
        </nav>

        <!-- Footer -->
        <div class="p-4 border-t border-rose-600">
            <!-- Network Status Indicators -->
            <div class="mb-3 space-y-2">
                <!-- Offline Mode -->
                <div v-if="!isOnline" :class="[
                    'px-4 py-2 bg-orange-500 rounded-lg flex items-center gap-2',
                    hasPendingWrites ? 'animate-pulse' : '',
                ]" data-cy="offline-indicator">
                    <i class="pi pi-wifi text-white"></i>
                    <span class="text-sm font-medium">
                        {{
                            hasPendingWrites
                                ? "Offline - modificări nesincronizate"
                                : "Offline"
                        }}
                    </span>
                </div>

                <!-- Syncing (online but pending) -->
                <div v-else-if="isSyncing" class="px-4 py-2 bg-blue-500 rounded-lg flex items-center gap-2" data-cy="syncing-indicator">
                    <i class="pi pi-spin pi-spinner text-white"></i>
                    <span class="text-sm font-medium">Se sincronizează...</span>
                </div>

                <!-- Just Synced (success message) -->
                <div v-else-if="justSynced" class="px-4 py-2 bg-green-500 rounded-lg flex items-center gap-2" data-cy="just-synced-indicator">
                    <i class="pi pi-check-circle text-white"></i>
                    <span class="text-sm font-medium">Sincronizat</span>
                </div>
            </div>

            <div class="flex items-center gap-3 px-4 py-2">
                <div class="w-10 h-10 bg-rose-600 rounded-full flex items-center justify-center">
                    <i class="pi pi-user text-white"></i>
                </div>
                <div class="flex-1">
                    <p class="text-sm font-medium" data-cy="doctor-name">{{ doctorName }}</p>
                    <p class="text-xs text-rose-200" data-cy="doctor-speciality">{{ doctorSpeciality }}</p>
                </div>
            </div>
        </div>
    </aside>
</template>

<script setup>
import { computed, inject, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { doc } from "firebase/firestore";
import { db } from "../../firebase/config.js";
import { useDocument } from "vuefire";

const router = useRouter();
const route = useRoute();

const networkStatus = inject("networkStatus", {
    isOnline: ref(true),
    hasPendingWrites: ref(false),
    isSyncing: ref(false),
    justSynced: ref(false),
});
const { isOnline, hasPendingWrites, isSyncing, justSynced } = networkStatus;

const props = defineProps({
    user: {
        type: Object,
        default: null,
    },
});

// Real-time doctor profile using VueFire
const doctorRef = computed(() => {
    if (!props.user) return null;
    return doc(db, "users", props.user.uid);
});
const doctorProfile = useDocument(doctorRef);
// Computed properties for doctor's name and speciality
const doctorName = computed(() => doctorProfile.value?.name || "");
const doctorSpeciality = computed(() => doctorProfile.value?.speciality || "");

const isActive = (name) => route.name === name;

const navClass = (name) => {
    const base =
        "w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 text-left";

    const active = "bg-white text-rose-800 shadow-lg pl-6";

    const inactive =
        "text-white/90 hover:bg-rose-800 hover:text-white hover:shadow-lg hover:pl-6";

    return [base, isActive(name) ? active : inactive];
};

const go = (name) => {
    if (!isActive(name)) {
        router.push({ name });
    }
}
</script>
